/**
 * Workspace risk level + Breaches & Risk pillar evaluation (Corporate / shared).
 */

/**
 * @param {object|null|undefined} res
 * @returns {boolean}
 */
export function isSanctionsHit(res) {
  if (!res || res.ui_bucket !== 'compliance_alert') return false;
  const level = String(res.risk_level || res.severity || '').toUpperCase();
  if (['PEP', 'SANCTIONED', 'WANTED', 'CRITICAL', 'HIGH'].includes(level)) return true;
  if (Array.isArray(res.alerts) && res.alerts.length > 0) {
    return res.alerts.some((a) => {
      const s = String(a?.severity || '').toUpperCase();
      return ['PEP', 'SANCTIONED', 'WANTED', 'CRITICAL', 'HIGH'].includes(s);
    });
  }
  return level !== '' && level !== 'CLEAN' && level !== 'NONE' && level !== 'LOW';
}

/**
 * @param {object|null|undefined} res
 * @returns {boolean}
 */
export function isCorporateBreachHit(res) {
  if (!res || res.ui_bucket !== 'corporate_breaches') return false;
  const n = Number(res.breached_platforms_count ?? res.details?.breached_platforms_count ?? 0);
  if (Number.isFinite(n) && n > 0) return true;
  if (Array.isArray(res.details) && res.details.length > 0) return true;
  return false;
}

/**
 * @param {object|null|undefined} res
 * @returns {boolean}
 */
export function isDarkWebThreat(res) {
  if (!res || res.ui_bucket !== 'darkweb_advisory') return false;
  const status = String(res.status || res.details?.status || '').toLowerCase();
  if (status === 'exposed' || status === 'threat' || status === 'hit') return true;
  const risk = String(res.risk_level || '').toUpperCase();
  if (['HIGH', 'CRITICAL', 'MEDIUM'].includes(risk)) return true;
  return Number(res.mentions_count || 0) > 0;
}

/**
 * Evaluate domain security audit issues (DMARC fail or active penalty score).
 *
 * @param {object|null|undefined} res
 * @returns {boolean}
 */
export function isDomainSecurityIssue(res) {
  if (!res || res.ui_bucket !== 'cyber_trust_score') return false;
  if (res.dmarc_secure === false) return true;
  if (res.penalty_active === true || res.details?.penalty_active === true) return true;
  const score = Number(res.risk_score || res.details?.risk_score || 0);
  return Number.isFinite(score) && score > 0;
}

/**
 * @param {object|null|undefined} res
 * @returns {boolean}
 */
export function isVirusTotalThreat(res) {
  if (!res || res.ui_bucket !== 'threat_intelligence') return false;
  const mal = Number(res.malicious || res.details?.malicious || 0);
  const sus = Number(res.suspicious || res.details?.suspicious || 0);
  return mal > 0 || sus > 0;
}

/**
 * Evaluate the five Breaches & Risk pillars from display results.
 *
 * @param {object[]} displayResults
 * @returns {{
 *   hasIssue: boolean,
 *   totalThreats: number,
 *   pillars: {
 *     sanctions: boolean,
 *     breaches: boolean,
 *     darkweb: boolean,
 *     domainSecurity: boolean,
 *     virusTotal: boolean,
 *   },
 * }}
 */
export function evaluateBreachesRiskPillars(displayResults = []) {
  const list = Array.isArray(displayResults) ? displayResults : [];
  const pillars = {
    sanctions: list.some(isSanctionsHit),
    breaches: list.some(isCorporateBreachHit),
    darkweb: list.some(isDarkWebThreat),
    domainSecurity: list.some(isDomainSecurityIssue),
    virusTotal: list.some(isVirusTotalThreat),
  };
  const totalThreats = Object.values(pillars).filter(Boolean).length;
  return {
    hasIssue: totalThreats > 0,
    totalThreats,
    pillars,
  };
}

/**
 * Unified RISK LEVEL for the fixed workspace metrics bar.
 *
 * @param {object[]} displayResults
 * @param {{ penalties?: { reason?: string }[] }} [opts]
 * @returns {{ level: 'CRITICAL'|'CLEAN', label: string, totalThreats: number, toneClass: string }}
 */
export function computeWorkspaceRiskLevel(displayResults = [], opts = {}) {
  const evald = evaluateBreachesRiskPillars(displayResults);
  const penaltyHits = Array.isArray(opts.penalties)
    ? opts.penalties.filter((p) => {
        const r = String(p?.reason || '').toLowerCase();
        return (
          r.includes('sanction') ||
          r.includes('pep') ||
          r.includes('wanted') ||
          r.includes('malicious') ||
          r.includes('breach') ||
          r.includes('dmarc')
        );
      }).length
    : 0;
  const totalThreats = Math.max(evald.totalThreats, penaltyHits > 0 ? evald.totalThreats || 1 : evald.totalThreats);
  const critical =
    evald.pillars.sanctions ||
    evald.pillars.virusTotal ||
    evald.pillars.breaches ||
    evald.pillars.darkweb ||
    evald.hasIssue;

  if (critical) {
    return {
      level: 'CRITICAL',
      label: `RISK LEVEL: CRITICAL (${Math.max(totalThreats, 1)})`,
      totalThreats: Math.max(totalThreats, 1),
      toneClass: 'border-rose-500/50 bg-rose-950/50 text-rose-100',
    };
  }
  return {
    level: 'CLEAN',
    label: 'RISK LEVEL: CLEAN',
    totalThreats: 0,
    toneClass: 'border-emerald-500/40 bg-emerald-950/35 text-emerald-100',
  };
}
